import { Badge, Group, LoadingOverlay } from '@mantine/core';
import React from 'react';

import { UserResponseType, useUsers } from '@portals/api/ui';
import { OverflowBadgeGroup } from '@portals/core';
import { ModalButton, usePermissionAccess } from '@portals/framework';
import { ReactComponent as LifebuoyIcon } from '@portals/icons/linear/lifebuoy.svg';
import { useOpenModal } from '@portals/redux';
import { AvatarCell, DateCell, RowMenuItems, SmartTable } from '@portals/table';
import { TableColumn } from '@portals/types';
import { timeAgo } from '@portals/utils';

import { UsersTableDetailsPanel } from './UsersTableDetailsPanel';
import { ToggleUserSupportSeatModalProps } from '../../../modals';

export function UsersTable() {
  const users = useUsers();
  const { isAdmin } = usePermissionAccess();
  const openModal = useOpenModal();

  const columns: TableColumn<UserResponseType>[] = [
    {
      dataField: 'name',
      text: 'Name',
      minWidth: 400,
      maxWidth: 400,
      isSticky: true,
      sort: true,
      formatter: (_, { name, email, has_support_seat }) => (
        <Group align="center" spacing="xl">
          <AvatarCell src={null} label={name} description={email} />

          {has_support_seat ? (
            <Badge
              bg="purple.0"
              radius="sm"
              color="gray.9"
              fw="400"
              sx={{ textTransform: 'none' }}
            >
              Support seat
            </Badge>
          ) : null}
        </Group>
      ),
    },
    {
      dataField: 'group_names',
      text: 'Groups',
      formatter: (_, row) => <OverflowBadgeGroup badges={row.group_names} />,
    },
    {
      dataField: 'created_at',
      text: 'Created at',
      sort: true,
      minWidth: 250,
      formatter: (_, row) => (
        <DateCell date={row.created_at} withTimeAgo={false} />
      ),
    },
    {
      dataField: 'last_seen_at',
      text: 'Last seen',
      sort: true,
      formatter: (_, { last_seen_at }) =>
        last_seen_at ? timeAgo(Date.parse(last_seen_at)) : 'never',
    },
    {
      dataField: 'sign_in_count',
      text: 'Logins count',
      sort: true,
      minWidth: 200,
    },
    {
      dataField: 'last_sign_in_at',
      text: 'Last login',
      sort: true,
      formatter: (_, { last_sign_in_at }) =>
        last_sign_in_at ? timeAgo(Date.parse(last_sign_in_at)) : 'never',
    },
    {
      dataField: 'last_ip',
      text: 'Last login IP',
      sort: true,
      minWidth: 200,
    },
  ];

  if (users.isFetching) {
    return <LoadingOverlay visible />;
  }

  return (
    <SmartTable<UserResponseType>
      keyField="id"
      name="partners.settings.users"
      data={users.data || []}
      columns={columns}
      noFilters
      exportParams={{ isEnabled: false }}
      noHeader={!isAdmin}
      noColumnsSelection
      noDataIndication={{ title: 'No users' }}
      additionalActions={() =>
        isAdmin ? (
          <ModalButton
            modalName="AddUserModal"
            label="Add User"
            data-testid="partners-add-user-btn"
          />
        ) : null
      }
      detailsPanel={{
        type: 'page',
        renderer: ({ row, onClose }) => (
          <UsersTableDetailsPanel user={row.original} onClose={onClose} />
        ),
      }}
      rowMenu={({ row, wrapperProps }) => {
        const hasSupportSeat = row.original.has_support_seat;

        return (
          <RowMenuItems
            wrapperProps={wrapperProps}
            items={[
              {
                id: 'toggle-support-seat',
                label: hasSupportSeat
                  ? 'Revoke Support Seat'
                  : 'Assign Support Seat',
                Icon: LifebuoyIcon,
                color: hasSupportSeat ? 'red' : undefined,
                onClick: () =>
                  openModal<ToggleUserSupportSeatModalProps['data']>(
                    'ToggleUserSupportSeatModal',
                    {
                      intent: hasSupportSeat ? 'revoke' : 'assign',
                      userId: row.original.id,
                    }
                  ),
              },
            ]}
          />
        );
      }}
    />
  );
}
